import { memo } from "react";
import { cn } from "@/lib/utils.ts";
import type { TrainingHomeModule } from "@/lib/api/training-simulator/training-simulator.ts";
import { getModuleTheme } from "@/lib/training-simulator/module-theme.ts";
import { TrainingLevelRow } from "./training-level-row.tsx";
import { EyeClosedIcon, EyeIcon } from "lucide-react";

interface TrainingModuleCardProps {
  module: TrainingHomeModule;
  isExpanded: boolean;
  onToggle: (moduleId: string) => void;
}

function TrainingModuleCardComponent({
  module,
  isExpanded,
  onToggle,
}: TrainingModuleCardProps) {
  const theme = getModuleTheme(module);

  return (
    <div className="w-full overflow-hidden rounded-3xl border border-surfaces-divider bg-surfaces-z3">
      <div
        className={cn(
          "relative flex flex-col gap-3 overflow-hidden p-6",
          theme.headerClassName,
        )}
      >
        <p className="text-body-3 text-text-primary/75">
          Module {module.position}
        </p>
        <p className="text-header-4 text-text-primary">{module.title}</p>
        {module.description ? (
          <p className="max-w-3xl text-body-3 text-text-primary/75">
            {module.description}
          </p>
        ) : null}

        {theme.headerAsset ? (
          <img
            src={theme.headerAsset}
            alt={module.title}
            className="absolute -right-16 -top-48 h-[500px] w-[500px] select-none"
          />
        ) : null}
      </div>

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-in-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="flex w-full flex-col">
            {module.levels.map((level) => (
              <TrainingLevelRow
                key={level.id}
                level={level}
                accentClassName={theme.headerClassName}
              />
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onToggle(module.id)}
        className="flex h-12 w-full items-center justify-center gap-2 bg-surfaces-z3 p-6 text-text-primary"
      >
        {isExpanded ? (
          <EyeClosedIcon className="size-4" />
        ) : (
          <EyeIcon className="size-4" />
        )}
        <span className="text-body-1">
          {isExpanded ? "Hide all" : "Show all"}
        </span>
      </button>
    </div>
  );
}

export const TrainingModuleCard = memo(TrainingModuleCardComponent);
